import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Monitor, Image as ImageIcon, Ban } from "lucide-react";

export default function Appearance() {
  const [hero, setHero] = useState(null);
  useEffect(() => { api.get("/content/settings").then((r) => setHero(r.data.home_hero)); }, []);
  const save = async () => {
    try { const { data } = await api.patch("/admin/settings/hero", hero); setHero(data.home_hero); toast.success("Homepage hero updated — refresh the site to see it live."); }
    catch { toast.error("Could not save"); }
  };
  if (!hero) return <AdminLayout><div className="p-10 text-paper/40">Loading…</div></AdminLayout>;
  const set = (k, v) => setHero({ ...hero, [k]: v });
  const TYPES = [["youtube", "YouTube", Monitor], ["video", "Video (mp4)", Monitor], ["image", "Image", ImageIcon], ["none", "None (solid)", Ban]];

  return (
    <AdminLayout>
      <div className="p-8 md:p-10 max-w-3xl">
        <div className="eyebrow text-heraldic mb-1">Appearance</div>
        <h1 className="font-serif text-4xl text-paper">Homepage hero</h1>
        <p className="text-paper/50 text-sm mt-1">Set a background video, an image, or none. Video is muted, autoplaying and looped — paste a direct .mp4 URL (e.g. from your media library).</p>

        <div className="mt-8">
          <div className="text-sm text-paper/70 mb-2">Background type</div>
          <div className="grid grid-cols-3 gap-2">
            {TYPES.map(([v, l, Icon]) => (
              <button key={v} data-testid={`hero-type-${v}`} onClick={() => set("media_type", v)}
                className={`flex flex-col items-center gap-2 py-5 rounded-sm border transition-colors ${hero.media_type === v ? "border-heraldic bg-heraldic/15 text-paper" : "border-white/10 text-paper/50 hover:text-paper"}`}>
                <Icon className="h-5 w-5" /> <span className="text-sm">{l}</span>
              </button>
            ))}
          </div>
        </div>

        {(hero.media_type === "video" || hero.media_type === "youtube") && (
          <div className="mt-6"><label className="text-sm text-paper/70">{hero.media_type === "youtube" ? "YouTube video ID (e.g. WQQyqqTeeTo)" : "Video URL (.mp4)"}</label>
            <Input data-testid="hero-video-url" value={hero.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder={hero.media_type === "youtube" ? "WQQyqqTeeTo" : "https://…/hero.mp4"} className="mt-1 bg-[#14161c] border-white/10 text-paper rounded-sm" /></div>
        )}
        {hero.media_type === "image" && (
          <div className="mt-6"><label className="text-sm text-paper/70">Image URL</label>
            <Input data-testid="hero-image-url" value={hero.image_url || ""} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…/hero.jpg" className="mt-1 bg-[#14161c] border-white/10 text-paper rounded-sm" /></div>
        )}

        <div className="mt-6"><label className="text-sm text-paper/70">Overlay darkness: {hero.overlay ?? 60}%</label>
          <input type="range" min="20" max="95" value={hero.overlay ?? 60} onChange={(e) => set("overlay", parseInt(e.target.value))} className="w-full mt-2 accent-heraldic" data-testid="hero-overlay" /></div>

        <div className="mt-6"><label className="text-sm text-paper/70">Headline override (optional — blank keeps the institutional positioning)</label>
          <Input data-testid="hero-headline" value={hero.headline_en || ""} onChange={(e) => set("headline_en", e.target.value)} placeholder="Readiness determines progression. Not age." className="mt-1 bg-[#14161c] border-white/10 text-paper rounded-sm" /></div>

        {(hero.media_type === "image" && hero.image_url) && (
          <div className="mt-6 relative h-40 rounded-sm overflow-hidden border border-white/10">
            <img src={hero.image_url} alt="preview" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-ink" style={{ opacity: (hero.overlay ?? 60) / 100 }} />
          </div>
        )}

        <button data-testid="hero-save" onClick={save} className="mt-8 bg-heraldic text-white px-8 py-3 rounded-sm hover:bg-heraldic-700 transition-colors">Save hero</button>
      </div>
    </AdminLayout>
  );
}
